import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { FileText, Download, Filter, RefreshCw, Search, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_META = {
  paid: { label: 'Betaald', color: '#16a34a', bg: '#d1fae5' },
  open: { label: 'Open', color: '#D4A017', bg: '#fef3c7' },
  cancelled: { label: 'Geannuleerd', color: '#dc2626', bg: '#fee2e2' },
};

const fmtEur = (n) => `€ ${Number(n || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function InvoicesPage() {
  const { api } = useAuth();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [reconciling, setReconciling] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [list, st] = await Promise.all([
        api('get', `/api/admin/invoices${filter ? `?status=${filter}` : ''}`),
        api('get', '/api/admin/invoices/stats').catch(() => ({ data: null })),
      ]);
      setItems(list.data.invoices || []);
      if (st.data) setStats(st.data);
    } catch (e) { toast.error('Laden mislukt'); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const reconcile = async () => {
    setReconciling(true);
    try {
      const r = await api('post', '/api/admin/invoices/reconcile');
      if (r.data.missing_invoice_count > 0) {
        toast.warning(`${r.data.missing_invoice_count} betalingen zonder factuur gevonden`);
      } else {
        toast.success('Alle betalingen hebben een factuur');
      }
    } catch (e) { toast.error('Reconcile mislukt'); }
    setReconciling(false);
  };

  const backfill = async () => {
    if (!window.confirm('Genereer alsnog facturen voor alle betaalde transacties zonder factuur?')) return;
    try {
      const r = await api('post', '/api/admin/invoices/backfill');
      toast.success(`${r.data.created_count} facturen gegenereerd`);
      load();
    } catch (e) { toast.error('Backfill mislukt'); }
  };

  const openInvoice = (id) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/invoices/${id}/html`;
    const token = localStorage.getItem('portal_token');
    // Open in nieuw tabblad met token in URL (eenvoudige aanpak voor PDF render)
    // Alternatief: maak een server-side route die cookie auth gebruikt
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.text())
      .then(html => {
        const w = window.open('', '_blank');
        w.document.write(html);
        w.document.close();
      })
      .catch(() => toast.error('Factuur openen mislukt'));
  };

  const filtered = items.filter(i => !search ||
    [i.invoice_number, i.customer?.email, i.customer?.company_name].some(x => (x || '').toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div data-testid="invoices-page">
      <div className="flex justify-between items-start flex-wrap gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-extrabold text-[#0f172a] flex items-center gap-2"><FileText size={20} className="text-[#D4A017]" /> Facturen</h1>
          <p className="text-sm text-[#64748b] mt-1">Boekhoudklare facturen — gegenereerd na elke betaalde transactie.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={reconcile} disabled={reconciling} data-testid="reconcile-btn" className="text-xs bg-white border border-slate-200 hover:border-[#D4A017] text-[#0f172a] font-bold px-3 py-2 rounded-lg flex items-center gap-1.5 disabled:opacity-50">
            <AlertCircle size={12} /> {reconciling ? 'Bezig…' : 'Reconcile'}
          </button>
          <button onClick={backfill} data-testid="backfill-btn" className="text-xs bg-[#D4A017] hover:bg-[#b88a13] text-[#0f172a] font-bold px-3 py-2 rounded-lg flex items-center gap-1.5">
            <RefreshCw size={12} /> Backfill ontbrekende
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <Stat label="Totaal facturen" value={stats.total || 0} />
          <Stat label="Betaalde omzet (incl. btw)" value={fmtEur(stats.by_status?.paid?.total || 0)} color="#16a34a" />
          <Stat label="Aantal betaald" value={stats.by_status?.paid?.count || 0} color="#16a34a" />
          <Stat label="Open" value={stats.by_status?.open?.count || 0} color="#D4A017" />
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-xl">
        <div className="p-3 border-b border-slate-200 flex gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-2.5 text-[#94a3b8]" />
            <input data-testid="invoice-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Zoek factuurnr / klant / email" className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg" />
          </div>
          <div className="flex gap-1">
            {['', 'paid', 'open', 'cancelled'].map(s => (
              <button key={s || 'all'} data-testid={`filter-${s || 'all'}`} onClick={() => setFilter(s)} className={`text-xs font-bold px-3 py-2 rounded-lg ${filter === s ? 'bg-[#0f172a] text-white' : 'bg-white border border-slate-200 text-[#64748b]'}`}>
                {s === '' ? 'Alle' : STATUS_META[s]?.label || s}
              </button>
            ))}
          </div>
        </div>
        <div className="divide-y divide-slate-100 max-h-[700px] overflow-y-auto">
          {loading ? <p className="text-center text-sm text-[#94a3b8] py-10">Laden...</p>
            : filtered.length === 0 ? <p className="text-center text-sm text-[#94a3b8] py-10">Geen facturen gevonden.</p>
            : filtered.map(inv => (
              <div key={inv.id} data-testid={`invoice-row-${inv.id}`} className="p-3 hover:bg-slate-50 flex justify-between items-center gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-sm text-[#0f172a]">{inv.invoice_number}</span>
                    <span style={{ background: STATUS_META[inv.status]?.bg, color: STATUS_META[inv.status]?.color }} className="text-[10px] font-bold px-2 py-0.5 rounded uppercase">{STATUS_META[inv.status]?.label || inv.status}</span>
                  </div>
                  <p className="text-xs text-[#64748b]">
                    {inv.customer?.company_name || inv.customer?.email} · {(inv.issue_date || '').slice(0, 10)} · {inv.lines?.[0]?.description}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-sm text-[#0f172a]">{fmtEur(inv.total_incl_vat)}</p>
                  <p className="text-[10px] text-[#94a3b8]">excl. btw: {fmtEur(inv.subtotal_ex_vat)}</p>
                </div>
                <button data-testid={`view-invoice-${inv.id}`} onClick={() => openInvoice(inv.id)} className="text-xs bg-white border border-slate-200 hover:border-[#D4A017] text-[#0f172a] font-bold px-3 py-2 rounded-lg flex items-center gap-1.5">
                  <Download size={12} /> Bekijk / PDF
                </button>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

const Stat = ({ label, value, color = '#0f172a' }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-3">
    <p className="text-xl font-extrabold" style={{ color }}>{value}</p>
    <p className="text-[10px] text-[#94a3b8] uppercase font-bold tracking-wide mt-1">{label}</p>
  </div>
);
